import { eventBus } from '../core/EventBus';
import { Game } from '../core/Game';
import { CurrencyManager } from '../economy/CurrencyManager';
import { Plant } from '../plants/Plant';
import { DetachedPanelState } from '../core/SaveManager';
import { iconSproutBar, iconHeart, iconTrending, iconPopout, iconCross } from '../icons';

type ConsoleTab = 'plants' | 'health' | 'currency';

export class StatusConsole {
  private el: HTMLDivElement;
  private isExpanded: boolean = false;
  private activeTab: ConsoleTab = 'plants';
  private detachedTabs: Set<ConsoleTab> = new Set();
  private detachedPanels: Map<ConsoleTab, HTMLDivElement> = new Map();
  private tabButtons: Map<ConsoleTab, HTMLButtonElement> = new Map();
  private pages: Map<ConsoleTab, HTMLDivElement> = new Map();
  private lastDragSave: number = 0;

  constructor(
    parent: HTMLElement,
    private plantsFn: () => Plant[],
    private currencyManager: CurrencyManager,
    _game: Game
  ) {
    this.el = document.createElement('div');
    this.el.className = 'status-console';
    this.buildDOM();
    parent.appendChild(this.el);

    eventBus.on('game:update', (_delta: number, gameTime: number) => {
      this.currencyManager.recordBalance(gameTime);
      this.refreshAll();
    });
  }

  toggle(willExpand: boolean): void {
    this.isExpanded = willExpand;
    if (this.isExpanded) {
      this.refreshAll();
    }
  }

  getDetachedState(): DetachedPanelState[] {
    return Array.from(this.detachedPanels.entries()).map(([tab, el]) => ({
      tab,
      left: el.offsetLeft,
      top: el.offsetTop,
    }));
  }

  restoreDetachedPanels(panels: DetachedPanelState[]): void {
    // Close all current detached panels
    for (const tab of this.detachedPanels.keys()) {
      this.closePanel(tab);
    }
    this.detachedTabs.clear();

    for (const p of panels) {
      this.restoreOnePanel(p.tab as ConsoleTab, p.left, p.top);
    }
  }

  private buildDOM(): void {
    const tabs = document.createElement('div');
    tabs.className = 'console-tabs';

    const tabDefs: { key: ConsoleTab; icon: string }[] = [
      { key: 'plants', icon: iconSproutBar({ size: 16 }) },
      { key: 'health', icon: iconHeart({ size: 16 }) },
      { key: 'currency', icon: iconTrending({ size: 16 }) },
    ];

    for (const def of tabDefs) {
      const btn = document.createElement('button');
      btn.className = `console-tab ${def.key === this.activeTab ? 'active' : ''}`;
      btn.dataset.tab = def.key;
      btn.innerHTML = def.icon;
      btn.title = def.key === 'plants' ? '植物统计' : def.key === 'health' ? '健康状态' : '金币曲线';
      btn.addEventListener('click', () => this.switchTab(def.key));
      tabs.appendChild(btn);
      this.tabButtons.set(def.key, btn);
    }

    const body = document.createElement('div');
    body.className = 'console-body';

    for (const def of tabDefs) {
      const page = document.createElement('div');
      page.className = `console-page ${def.key === this.activeTab ? 'active' : ''}`;
      page.dataset.page = def.key;
      body.appendChild(page);
      this.pages.set(def.key, page);
    }

    this.el.appendChild(tabs);
    this.el.appendChild(body);
  }

  private switchTab(tab: ConsoleTab): void {
    if (this.detachedTabs.has(tab)) return;
    this.activeTab = tab;
    for (const [key, btn] of this.tabButtons) {
      btn.classList.toggle('active', key === tab);
    }
    for (const [key, page] of this.pages) {
      page.classList.toggle('active', key === tab);
    }
    this.refreshPage(tab);
  }

  private refreshAll(): void {
    for (const tab of this.pages.keys()) {
      if (!this.detachedTabs.has(tab)) {
        this.refreshPage(tab);
      }
    }
    for (const [tab, panel] of this.detachedPanels) {
      const contentEl = panel.querySelector('.dp-content') as HTMLDivElement;
      if (contentEl) {
        contentEl.innerHTML = this.getPageContent(tab);
      }
    }
  }

  private refreshPage(tab: ConsoleTab): void {
    const page = this.pages.get(tab);
    if (!page) return;
    page.innerHTML = this.getPageContent(tab);

    const detachBtn = document.createElement('button');
    detachBtn.className = 'detach-btn';
    detachBtn.innerHTML = iconPopout({ size: 12 });
    detachBtn.title = '分离为浮动面板';
    detachBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.detachTab(tab);
    });
    page.appendChild(detachBtn);

    const clearBtn = page.querySelector('.currency-clear-btn') as HTMLButtonElement;
    if (clearBtn) {
      clearBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.currencyManager.clearHistory();
        this.refreshPage('currency');
      });
    }
  }

  private getPageContent(tab: ConsoleTab): string {
    switch (tab) {
      case 'plants': return this.renderPlants();
      case 'health': return this.renderHealth();
      case 'currency': return this.renderCurrency();
    }
  }

  private renderPlants(): string {
    const plants = this.plantsFn();
    if (plants.length === 0) {
      return '<div class="console-empty">还没有植物</div>';
    }

    const stageNames = ['种子', '萌芽', '幼苗', '抽穗', '盛花'];
    const counts = stageNames.map((_, i) => plants.filter(p => p.currentStageIndex === i).length);
    const maxCount = Math.max(1, ...counts);

    const speciesCounts: Record<string, number> = {};
    for (const p of plants) {
      speciesCounts[p.species.name] = (speciesCounts[p.species.name] || 0) + 1;
    }
    const speciesLines = Object.entries(speciesCounts)
      .map(([name, count]) => `<span class="stat-line">${name}: ${count}株</span>`)
      .join(' · ');

    const chartRows = stageNames.map((name, i) => {
      const count = counts[i];
      const pct = (count / maxCount) * 100;
      return `
        <div class="chart-row">
          <span class="chart-label">${name}</span>
          <div class="chart-bar-track"><div class="chart-bar-fill" style="width:${pct}%"></div></div>
          <span class="chart-value">${count}</span>
        </div>
      `;
    }).join('');

    return `
      <div class="stat-summary">${speciesLines}</div>
      <div class="chart-title">生长阶段</div>
      <div class="chart-container">${chartRows}</div>
    `;
  }

  private renderHealth(): string {
    const plants = this.plantsFn();
    if (plants.length === 0) {
      return '<div class="console-empty">还没有植物</div>';
    }

    const stateOrder: Array<{ key: string; label: string; color: string }> = [
      { key: 'thriving', label: '蓬勃', color: '#4a6b3a' },
      { key: 'lazy', label: '慵懒', color: '#b89830' },
      { key: 'withering', label: '枯萎', color: '#b06038' },
      { key: 'dormant', label: '休眠', color: '#7a6a5a' },
    ];

    const counts = stateOrder.map(s => plants.filter(p => p.state === s.key).length);
    const maxCount = Math.max(1, ...counts);
    const avgHealth = plants.reduce((sum, p) => sum + p.health, 0) / plants.length;

    let healthColor = '#4a6b3a';
    if (avgHealth <= 20) healthColor = '#a05050';
    else if (avgHealth <= 45) healthColor = '#b06038';
    else if (avgHealth <= 70) healthColor = '#b89830';

    const chartRows = stateOrder.map((s, i) => {
      const count = counts[i];
      const pct = (count / maxCount) * 100;
      return `
        <div class="chart-row">
          <span class="chart-label">${s.label}</span>
          <div class="chart-bar-track"><div class="chart-bar-fill" style="width:${pct}%;background:${s.color}"></div></div>
          <span class="chart-value">${count}</span>
        </div>
      `;
    }).join('');

    return `
      <div class="health-summary">
        <span class="health-avg-label">平均健康</span>
        <div class="sc-health-bar"><div class="sc-health-fill" style="width:${avgHealth}%;background:${healthColor}"></div></div>
        <span class="health-avg-value" style="color:${healthColor}">${Math.round(avgHealth)}</span>
      </div>
      <div class="chart-title">状态分布</div>
      <div class="chart-container">${chartRows}</div>
    `;
  }

  private renderCurrency(): string {
    const history = this.currencyManager.getBalanceHistory();
    const current = Math.floor(this.currencyManager.balance);

    if (history.length < 2) {
      return `
        <div class="currency-current">💰 ${current}</div>
        <div class="console-empty" style="margin-top:8px;">数据正在收集中…</div>
        <button class="journal-btn currency-clear-btn" style="margin-top:10px;width:100%;">清空记录</button>
      `;
    }

    const minBal = Math.min(...history.map(h => h.balance));
    const maxBal = Math.max(...history.map(h => h.balance));
    const range = Math.max(1, maxBal - minBal);
    const padding = range * 0.1;
    const yMin = Math.max(0, minBal - padding);
    const yMax = maxBal + padding;
    const yRange = yMax - yMin;

    const svgW = 240;
    const svgH = 70;
    const points = history.map((h, i) => {
      const x = (i / (history.length - 1)) * svgW;
      const y = svgH - ((h.balance - yMin) / yRange) * svgH;
      return `${x},${y}`;
    }).join(' ');

    return `
      <div class="currency-current">💰 ${current}</div>
      <div class="currency-chart">
        <svg viewBox="0 0 ${svgW} ${svgH}" width="100%" height="70">
          <polyline points="${points}" fill="none" stroke="#8b5e3c" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" opacity="0.7"/>
          ${history.map((h, i) => {
            const x = (i / (history.length - 1)) * svgW;
            const y = svgH - ((h.balance - yMin) / yRange) * svgH;
            return `<circle cx="${x}" cy="${y}" r="2" fill="#8b5e3c" opacity="0.5"/>`;
          }).join('')}
        </svg>
      </div>
      <button class="journal-btn currency-clear-btn" style="margin-top:6px;width:100%;">清空记录</button>
    `;
  }

  // ── Detach / Close (user actions → auto-save) ──

  private detachTab(tab: ConsoleTab): void {
    if (this.detachedTabs.has(tab)) return;
    this.detachPanel(tab);
    this.saveDetachedState();
  }

  closeDetachedPanel(tab: ConsoleTab): void {
    this.closePanel(tab);
    this.saveDetachedState();
  }

  // ── Core detach / close (no save) ──

  private restoreOnePanel(tab: ConsoleTab, left: number, top: number): void {
    this.detachPanel(tab, left, top);
  }

  private detachPanel(tab: ConsoleTab, savedLeft?: number, savedTop?: number): void {
    this.detachedTabs.add(tab);

    const btn = this.tabButtons.get(tab);
    if (btn) btn.classList.add('detached');

    const page = this.pages.get(tab);
    if (page) page.classList.remove('active');

    const available = (['plants', 'health', 'currency'] as ConsoleTab[]).find(t => !this.detachedTabs.has(t));
    if (available && this.activeTab === tab) {
      this.switchTab(available);
    }

    const panel = this.createDetachedPanel(tab, savedLeft, savedTop);
    document.body.appendChild(panel);
    this.detachedPanels.set(tab, panel);
  }

  private closePanel(tab: ConsoleTab): void {
    this.detachedTabs.delete(tab);
    const btn = this.tabButtons.get(tab);
    if (btn) btn.classList.remove('detached');

    const panel = this.detachedPanels.get(tab);
    if (panel) {
      panel.remove();
      this.detachedPanels.delete(tab);
    }

    if (this.isExpanded && this.activeTab === tab) {
      this.switchTab(tab);
    }
  }

  private createDetachedPanel(tab: ConsoleTab, savedLeft?: number, savedTop?: number): HTMLDivElement {
    const panel = document.createElement('div');
    panel.className = 'detached-panel';

    const titleMap: Record<ConsoleTab, string> = {
      plants: '植物统计',
      health: '健康状态',
      currency: '金币曲线',
    };

    panel.innerHTML = `
      <div class="dp-header">
        <span class="dp-title">${titleMap[tab]}</span>
        <button class="dp-close">${iconCross({ size: 14 })}</button>
      </div>
      <div class="dp-content">${this.getPageContent(tab)}</div>
    `;

    const header = panel.querySelector('.dp-header') as HTMLDivElement;
    const closeBtn = panel.querySelector('.dp-close') as HTMLButtonElement;

    closeBtn.addEventListener('click', () => this.closeDetachedPanel(tab));

    this.makeDraggable(panel, header, tab);

    if (savedLeft !== undefined) {
      panel.style.left = `${savedLeft}px`;
      panel.style.top = `${savedTop}px`;
    } else {
      const rect = this.el.getBoundingClientRect();
      panel.style.left = `${rect.right + 16}px`;
      panel.style.top = `${rect.top + this.detachedPanels.size * 24}px`;
    }

    return panel;
  }

  private saveDetachedState(): void {
    eventBus.emit('game:save');
  }

  private makeDraggable(el: HTMLElement, handle: HTMLElement, _tab: ConsoleTab): void {
    let dragging = false;
    let startX = 0;
    let startY = 0;
    let panelX = 0;
    let panelY = 0;

    const onMouseDown = (e: MouseEvent) => {
      if ((e.target as HTMLElement).closest('.dp-close')) return;
      e.preventDefault();
      dragging = true;
      startX = e.clientX;
      startY = e.clientY;
      panelX = el.offsetLeft;
      panelY = el.offsetTop;
      handle.style.cursor = 'grabbing';
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!dragging) return;
      el.style.left = `${panelX + e.clientX - startX}px`;
      el.style.top = `${panelY + e.clientY - startY}px`;
    };

    const onMouseUp = () => {
      if (!dragging) return;
      dragging = false;
      handle.style.cursor = 'grab';
      const now = Date.now();
      if (now - this.lastDragSave > 2000) {
        this.lastDragSave = now;
        this.saveDetachedState();
      }
    };

    handle.addEventListener('mousedown', onMouseDown);
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }
}
